<template>
  <div class="w-full h-full flex flex-col max-h-full overflow-hidden">
    <div class="w-full text-center pb-4">
      <tx-tabs v-model="compareType" :tabs="availableComparers" />
    </div>

    <div class="flex flex-row gap-2 mb-2">
      <!-- Column 1 -->
      <tx-select
        v-model="asset1"
        :data="availableAssets1"
        display-prop="label"
        value-prop="asset"
        placeholder="Select first asset to compare"
      />
      <tx-button type="icon" faicon="fa-light fa-arrows-left-right-to-line" @click="doSwap(1)" />
      <!-- Column 2 -->
      <tx-select
        v-model="asset2"
        :data="availableAssets2"
        display-prop="label"
        value-prop="asset"
        placeholder="Select second asset to compare"
      />
      <tx-button v-if="compareType === 'side-by-side-3' || compareType === 'side-by-side-4'" type="icon" faicon="fa-light fa-arrows-left-right-to-line" @click="doSwap(2)" />
      <!-- Column 3 -->
      <tx-select
        v-if="compareType === 'side-by-side-3' || compareType === 'side-by-side-4'"
        v-model="asset3"
        :data="availableAssets3"
        display-prop="label"
        value-prop="asset"
        placeholder="Select second asset to compare"
      />
      <tx-button v-if="compareType === 'side-by-side-4'" type="icon" faicon="fa-light fa-arrows-left-right-to-line" @click="doSwap(3)" />
      <!-- Column 4 -->
      <tx-select
        v-if="compareType === 'side-by-side-4'"
        v-model="asset4"
        :data="availableAssets4"
        display-prop="label"
        value-prop="asset"
        placeholder="Select second asset to compare"
      />
    </div>
    <div v-if="compareType !== 'slider'" class="flex flex-auto flex-row gap-2 ">
      <div class="flex-1">
        <pan-zoom height="100%" class="h-full">
          <img v-if="url1" :src="url1" alt="Asset 1" class="h-full object-contain">
        </pan-zoom>
      </div>
      <div class="flex-1">
        <pan-zoom height="100%" class="h-full">
          <img v-if="url2" :src="url2" alt="Asset 2" class="h-full object-contain">
        </pan-zoom>
      </div>
      <div v-if="compareType === 'side-by-side-3' || compareType === 'side-by-side-4'" class="flex-1">
        <pan-zoom height="100%" class="h-full">
          <img v-if="url3" :src="url3" alt="Asset 3" class="h-full object-contain">
        </pan-zoom>
      </div>
      <div v-if="compareType === 'side-by-side-4'" class="flex-1 flex flex-col">
        <pan-zoom height="100%" class="h-full">
          <img v-if="url4" :src="url4" alt="Asset 4" class="h-full object-contain">
        </pan-zoom>
      </div>
    </div>
    <div v-else class="overflow-hidden flex">
      <asset-overlay-comparer
        v-if="asset1Record && asset2Record"
        :left-image="url1"
        :right-image="url2"
        :left-image-alt="asset1Record.document.Metadata?.ColorwayName || asset1Record.document.FileName"
        :right-image-alt="asset2Record.document.Metadata?.ColorwayName || asset2Record.document.FileName"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import AssetOverlayComparer from './AssetOverlayComparer.vue'
import type { SearchAssetModel } from '@/api/dune/model/searchAssetModel'
import TxSelect from '@/shared/components/TxSelect.vue'
import appConfig from '@/services/appConfig'
import TxTabs from '@/shared/components/TxTabs.vue'
import PanZoom from '@/shared/components/PanZoom.vue'
import TxButton from '@/shared/components/TxButton.vue'

interface IProps {
  assets: SearchAssetModel[]
  context: string
  contextKey: string
}

const props = defineProps<IProps>()

const compareType = ref<'slider' | 'side-by-side-2' | 'side-by-side-3' | 'side-by-side-4'>('side-by-side-2')
const asset1 = ref<string | null>(null)
const asset2 = ref<string | null>(null)
const asset3 = ref<string | null>(null)
const asset4 = ref<string | null>(null)

const asset1Record = computed(() => props.assets.find(asset => asset.document.Id === asset1.value) || null)
const asset2Record = computed(() => props.assets.find(asset => asset.document.Id === asset2.value) || null)
const asset3Record = computed(() => props.assets.find(asset => asset.document.Id === asset3.value) || null)
const asset4Record = computed(() => props.assets.find(asset => asset.document.Id === asset4.value) || null)

const availableComparers = computed(() => {
  const res = [{ key: 'slider', label: 'Overlay Slider', visible: true }, { key: 'side-by-side-2', label: 'Side by Side: 2', visible: true }]
  if (props.assets.length > 2) {
    res.push({ key: 'side-by-side-3', label: 'Side by Side: 3', visible: true })
  }
  if (props.assets.length > 3) {
    res.push({ key: 'side-by-side-4', label: 'Side by Side: 4', visible: true })
  }
  return res
})

const assetsToCompare = computed(() => {
  let res = 2
  if (compareType.value === 'side-by-side-3') {
    res = 3
  }
  else if (compareType.value === 'side-by-side-4') {
    res = 4
  }
  return res
})

const availableAssets1 = computed(() => {
  return props.assets.filter(asset => asset.document.Id !== asset2.value && (assetsToCompare.value < 3 || asset.document.Id !== asset3.value) && (assetsToCompare.value < 4 || asset.document.Id !== asset4.value)).map(asset => ({
    label: asset.document.Metadata?.ColorwayName || asset.document.FileName,
    asset: asset.document.Id,
  }))
})

const availableAssets2 = computed(() => {
  return props.assets.filter(asset => asset.document.Id !== asset1.value && (assetsToCompare.value < 3 || asset.document.Id !== asset3.value) && (assetsToCompare.value < 4 || asset.document.Id !== asset4.value)).map(asset => ({
    label: asset.document.Metadata?.ColorwayName || asset.document.FileName,
    asset: asset.document.Id,
  }))
})

const availableAssets3 = computed(() => {
  return props.assets.filter(asset => asset.document.Id !== asset1.value && asset.document.Id !== asset2.value && (assetsToCompare.value < 4 || asset.document.Id !== asset4.value)).map(asset => ({
    label: asset.document.Metadata?.ColorwayName || asset.document.FileName,
    asset: asset.document.Id,
  }))
})

const availableAssets4 = computed(() => {
  return props.assets.filter(asset => asset.document.Id !== asset1.value && asset.document.Id !== asset2.value && asset.document.Id !== asset3.value).map(asset => ({
    label: asset.document.Metadata?.ColorwayName || asset.document.FileName,
    asset: asset.document.Id,
  }))
})

const url1 = computed(() => {
  if (!asset1.value || !asset1Record.value) { return '' }
  const params = new URLSearchParams()
  params.set('Context', props.context)
  params.set('ContextKey', props.contextKey)
  params.set('trim', 'true')
  params.set('f', 'webp')

  return `${appConfig.AssetsUrl}/assets/content/${asset1Record.value.document.StorageFile.toUpperCase()}?${params.toString()}`
})

const url2 = computed(() => {
  if (!asset2.value || !asset2Record.value) { return '' }
  const params = new URLSearchParams()
  params.set('Context', props.context)
  params.set('ContextKey', props.contextKey)
  params.set('trim', 'true')
  params.set('f', 'webp')

  return `${appConfig.AssetsUrl}/assets/content/${asset2Record.value.document.StorageFile.toUpperCase()}?${params.toString()}`
})

const url3 = computed(() => {
  if (!asset3.value || !asset3Record.value) { return '' }
  const params = new URLSearchParams()
  params.set('Context', props.context)
  params.set('ContextKey', props.contextKey)
  params.set('trim', 'true')
  params.set('f', 'webp')

  return `${appConfig.AssetsUrl}/assets/content/${asset3Record.value.document.StorageFile.toUpperCase()}?${params.toString()}`
})

const url4 = computed(() => {
  if (!asset4.value || !asset4Record.value) { return '' }
  const params = new URLSearchParams()
  params.set('Context', props.context)
  params.set('ContextKey', props.contextKey)
  params.set('trim', 'true')
  params.set('f', 'webp')

  return `${appConfig.AssetsUrl}/assets/content/${asset4Record.value.document.StorageFile.toUpperCase()}?${params.toString()}`
})

onMounted(() => {
  if (props.assets.length > 0) {
    asset1.value = props.assets[0].document.Id
    asset2.value = props.assets[1]?.document.Id || null
  }
})

function init() {
  if (props.assets.length < 4 && compareType.value === 'side-by-side-4') {
    compareType.value = 'side-by-side-3'
  }
  if (props.assets.length < 3 && compareType.value === 'side-by-side-3') {
    compareType.value = 'side-by-side-2'
  }
  asset1.value = props.assets[0]?.document?.Id || null
  asset2.value = props.assets[1]?.document?.Id || null
  asset3.value = props.assets[2]?.document?.Id || null
  asset4.value = props.assets[3]?.document?.Id || null
}

function doSwap(column: number) {
  if (column === 1) {
    [asset1.value, asset2.value] = [asset2.value, asset1.value]
  }
  else if (column === 2) {
    [asset2.value, asset3.value] = [asset3.value, asset2.value]
  }
  else if (column === 3) {
    [asset3.value, asset4.value] = [asset4.value, asset3.value]
  }
}

defineExpose({
  init,
})
</script>
